/**
 * The few role NAMES the UI still refers to by name.
 *
 * Not a catalogue and not an authorization model: roles are data, the backend
 * owns them, and a new role must not require a frontend change. `OWNER` is the
 * only member with a live consumer (`lib/accounts.ts`, which reads it to label
 * an owner account); the rest are kept because they are real backend names and
 * removing them would be unrelated churn.
 */
export const ROLES = {
  OWNER: 'OWNER',
  MANAGER: 'MANAGER',
  HR: 'HR',
  EMPLOYEE: 'EMPLOYEE',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: readonly Role[] = Object.values(ROLES);

/*
 * There is deliberately NO role hierarchy in this file.
 *
 * Seniority is `roles.level` in the backend, and which roles a given account
 * may hand out is answered by `GET /roles/assignable`. A second ranking here
 * would be a competing hierarchy that drifts the moment a level changes — the
 * one that used to live at this spot ranked five roles 1-5 and named an ADMIN
 * role the backend does not have, while the real hierarchy spans 22 roles from
 * 10 to 100. Ask the API; do not rank roles on the client.
 */

/** Does this account hold the given role? Membership only — never seniority. */
export function hasRole(userRoles: readonly Role[] | undefined, role: Role): boolean {
  return userRoles?.includes(role) ?? false;
}

export function hasAnyRole(
  userRoles: readonly Role[] | undefined,
  roles: readonly Role[],
): boolean {
  return roles.some((role) => hasRole(userRoles, role));
}

export function isOwner(userRoles: readonly Role[] | undefined): boolean {
  return hasRole(userRoles, ROLES.OWNER);
}
